<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { useTr } from '@/composables/useTr'

const app = useAppStore()
const route = useRoute()
const { tr, locale } = useTr()

const dropdownOpen = ref(false)
const dropdownRoot = ref<HTMLElement | null>(null)

function onDocClick(e: MouseEvent) {
  if (!dropdownRoot.value) return
  if (!dropdownRoot.value.contains(e.target as Node)) dropdownOpen.value = false
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))

const pageLabelMap: Record<string, { zh: string; en: string }> = {
  dashboard:     { zh: '工作台',         en: 'Workbench' },
  targets:       { zh: '目标',           en: 'Targets' },
  inventory:     { zh: '资产',           en: 'Asset Inventory' },
  findings:      { zh: '安全线索',       en: 'Findings' },
  'ai-triage':   { zh: 'AI 复核',         en: 'AI Triage' },
  monitor:       { zh: '监控',           en: 'Monitoring' },
  reports:       { zh: '报告',           en: 'Reports' },
  integrations:  { zh: '集成',           en: 'Integrations' },
  help:          { zh: '帮助中心',       en: 'Help Center' },
  license:       { zh: 'License / 订阅', en: 'License / Subscription' },
  appeals:       { zh: '报告反驳助手',   en: 'Report Rebuttal' },
  system:        { zh: '系统状态',       en: 'System Status' },
  'audit-log':   { zh: '操作日志',       en: 'Audit Log' },
  's-workspace': { zh: '工作区',         en: 'Workspace' },
  's-pipeline':  { zh: '扫描调度',       en: 'Scan Scheduling' },
  's-tools':     { zh: '工具市场',       en: 'Tool Marketplace' },
  's-proxies':   { zh: '代理池',         en: 'Proxy Pool' },
  's-ai':        { zh: 'AI 模型',        en: 'AI Models' },
  's-notify':    { zh: '通知',           en: 'Notifications' },
  's-network':   { zh: '网络诊断',       en: 'Network Diagnostics' },
  's-i18n':      { zh: '国际化',         en: 'i18n' },
}

const breadcrumb = computed<[string, string]>(() => {
  const pid = (route.meta?.pageId as string) ?? 'dashboard'
  const label = pageLabelMap[pid] ?? { zh: pid, en: pid }
  const text = locale.value === 'en' ? label.en : label.zh
  if (pid.startsWith('s-')) return [tr('设置', 'Settings'), text]
  return ['GhostMap-CN', text]
})

function openModal(name: 'changelog' | 'feedback' | 'contact' | 'subscription' | 'sysStatus' | 'auditLog') {
  dropdownOpen.value = false
  if (name === 'changelog') app.changelogModal = true
  if (name === 'feedback') app.feedbackModal = true
  if (name === 'contact') app.contactModal = true
  if (name === 'subscription') app.subscriptionModal = true
  if (name === 'sysStatus') app.sysStatusModal = true
  if (name === 'auditLog') app.auditLogModal = true
}
</script>

<template>
  <div class="topbar">
    <div class="breadcrumb">
      <span>{{ breadcrumb[0] }}</span>
      <span class="sep">/</span>
      <span class="current">{{ breadcrumb[1] }}</span>
    </div>
    <div class="topbar-right">
      <div class="theme-toggle">
        <button :class="locale === 'zh' ? 'on' : ''" @click="app.setLocale('zh')" :title="tr('中文', 'Chinese')" style="width:auto; padding:0 10px; font-size:11px; font-weight:600;">中</button>
        <button :class="locale === 'en' ? 'on' : ''" @click="app.setLocale('en')" title="English" style="width:auto; padding:0 10px; font-size:11px; font-weight:600;">EN</button>
      </div>

      <div class="theme-toggle">
        <button :class="app.theme === 'light' ? 'on' : ''" @click="app.setTheme('light')" :title="tr('亮色', 'Light')">
          <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
        </button>
        <button :class="app.theme === 'dark' ? 'on' : ''" @click="app.setTheme('dark')" :title="tr('暗色', 'Dark')">
          <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
        </button>
      </div>

      <button class="upgrade-pill" @click="app.proModal = true">
        <span class="lc-dot" />
        <span>{{ app.isPro ? 'Pro · 6/15' : tr('免费版', 'Free') }}</span>
        <span class="hint">{{ app.isPro ? tr('续费', 'Renew') : tr('升级', 'Upgrade') }}</span>
        <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
      </button>

      <div class="dropdown" ref="dropdownRoot">
        <div class="avatar-btn" @click="dropdownOpen = !dropdownOpen">D</div>
        <div v-show="dropdownOpen" class="dropdown-panel">
          <div class="dd-header">
            <div class="name">{{ tr('您的工作区', 'Your Workspace') }}</div>
            <div class="sub">you@example.com</div>
          </div>
          <div class="dd-item" @click="openModal('changelog')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>{{ tr('更新日志', 'Changelog') }}</span>
          </div>
          <div class="dd-item" @click="openModal('feedback')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4.5L6 21l1.5-7.5L2 9h7z"/></svg>
            <span>{{ tr('提建议', 'Suggestions') }}</span>
          </div>
          <div class="dd-divider" />
          <div class="dd-item" @click="openModal('contact')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
            <span>{{ tr('联系客服', 'Contact Support') }}</span>
          </div>
          <div class="dd-divider" />
          <div class="dd-item" @click="openModal('subscription')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>{{ tr('订阅状态', 'Subscription') }}</span>
          </div>
          <div v-show="app.advancedMode || app.isPro" class="dd-item" @click="openModal('sysStatus')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/></svg>
            <span>{{ tr('系统状态', 'System Status') }}</span>
          </div>
          <div v-show="app.advancedMode || app.isPro" class="dd-item" @click="openModal('auditLog')">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>
            <span>{{ tr('操作日志', 'Audit Log') }}</span>
          </div>
          <div class="dd-divider" />
          <div class="dd-item">
            <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
            <span>{{ tr('退出登录', 'Sign Out') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
